/**
 * Wallet conversions — dashboard surface.
 *
 * These routes are mounted under `/dashboard/wallets/conversions` on the
 * backend (JWT + permission-gated). Distinct from `/wallets/conversions`,
 * which is the merchant-secret-key API surface used by external integrators.
 */

import { instance } from '../config/api';

/** @typedef {Object} ConversionQuote
 * @property {string} quote_id
 * @property {string} from_currency
 * @property {string} to_currency
 * @property {number} from_amount
 * @property {number} to_amount
 * @property {number} exchange_rate
 * @property {string} expires_at  ISO-8601
 */

/** @typedef {'INITIATED' | 'SUCCESS' | 'FAILED'} WalletConversionStatus */

/** @typedef {Object} WalletConversion
 * @property {string} conversion_id
 * @property {string} rate_id
 * @property {WalletConversionStatus} status
 * @property {string} from_currency
 * @property {string} to_currency
 * @property {number} from_amount
 * @property {number} to_amount
 * @property {string} from_display
 * @property {string} to_display
 * @property {number} rate_used
 * @property {string|null} reference
 * @property {string|null} [failure_reason]
 * @property {string} created_at  ISO-8601
 */

/** @typedef {Object} ListWalletConversionsQuery
 * @property {number} [limit]
 * @property {number} [offset]
 * @property {string} [reference]
 */

const BASE_PATH = 'dashboard/wallets/conversions';

/**
 * Generate a conversion quote. The returned `quote_id` is short-lived
 * (the backend serves an `expires_at` in the response) and must be passed
 * back to `createWalletConversion` before it expires.
 *
 * @param {{ from_currency: string, to_currency: string, amount: number }} payload
 * @returns {Promise<{ status: string, data: ConversionQuote }>}
 */
const createConversionQuote = async (payload) => {
  const { data } = await instance.post(`${BASE_PATH}/quotes`, payload);
  return data;
};

/**
 * Confirm a previously-generated quote and execute the conversion.
 *
 * @param {{ quote_id: string, reference?: string }} payload
 * @returns {Promise<{ status: string, data: WalletConversion }>}
 */
const createWalletConversion = async (payload) => {
  const { data } = await instance.post(BASE_PATH, payload);
  return data;
};

/**
 * Paginated list of past conversions for the merchant. Mirrors the backend's
 * `{ data, meta: { limit, hasNext, count } }` envelope.
 *
 * @param {ListWalletConversionsQuery} [query]
 * @returns {Promise<{ status: string, data: WalletConversion[], meta: { limit: number, hasNext: boolean, count: number } }>}
 */
const fetchWalletConversions = async (query = {}) => {
  const { data } = await instance.get(BASE_PATH, { params: query });
  return data;
};

export { createConversionQuote, createWalletConversion, fetchWalletConversions };
